import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { StyledFactory } from '../../mixins/Styled.js';
import { OneUxElement } from '../../OneUxElement.js';
import { style } from './style.js';
import { indicationData } from './types.js';
import { log } from '../../utils/log.js';
import { Boundary } from '../../visualizations/common/mixins/Boundary.js';
import { DistributionAccessibleTable } from '../../visualizations/common/fragments/DistributionAccessibleTable.js';
import { DistributionPopout } from '../../visualizations/common/fragments/DistributionPopout.js';
import { Compact } from '../../mixins/Compact.js';
import { Label } from '../../mixins/Label.js';
import { keyCodes } from '../../utils.js';
import { Focusable } from '../../mixins/Focusable.js';
import { Weight } from '../../mixins/Weight.js';
import { getLanguage, language } from './lang.js';
import { ShapeArc } from './fragments/ShapeArc.js';
import { ShapeBar } from './fragments/ShapeBar.js';
import { Implicit } from '../../mixins/Implicit.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import palette from '../../generated/json/palette/palette.json';
import { until } from 'lit/directives/until.js';
import { SlotController } from '../../controllers/SlotController.js';
import { DistributionStaticLegend } from '../common/fragments/DistributionStaticLegend.js';
const Styled = StyledFactory(style);
const BaseClass = Implicit(Weight(Focusable(Compact(Boundary(Label(Styled(OneUxElement)))))));
export class OneUxIndicationElement extends BaseClass {
  static get elementType() {
    return 'one-ux-indication';
  }
  @property({
    type: String
  })
  public accessor shape!: 'arc' | 'bar';
  @property({
    type: Array
  })
  public accessor data: indicationData[] = [];
  @property({
    type: Number
  })
  public accessor value = 0;
  @property({
    type: String
  })
  public accessor unit!: string;
  @property({
    type: Boolean,
    reflect: true
  })
  public accessor static = false;
  @state()
  private accessor _mouseHover = false;
  @state()
  private accessor _keyboardOpen = false;
  protected _slots: SlotController = new SlotController(this, {
    slots: ['summary', 'description']
  });
  protected get _isNonZeroTotal() {
    return this.data.some(x => !x.exclude && x.value > 0);
  }
  constructor() {
    super();
    this.addEventListener('mouseenter', () => {
      this._mouseHover = true;
    });
    this.addEventListener('mouseleave', () => {
      this._mouseHover = false;
    });
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('blur', () => {
      this._keyboardOpen = false;
    });
  }
  guardedRender() {
    if (!this.shape) {
      log.error('<one-ux-indication> Missing shape, not rendering.');
      return;
    }
    const language = getLanguage(this);
    const indication = this.data.map(x => ({
      ...x,
      color: x.color in palette ? `var(--one-ux-palette--${x.color})` : x.color
    }));
    const {
      metadata,
      distribution
    } = this.#extractMetadataAndDistribution(indication);
    const highlightedSegments = this.data.filter(x => x.highlighted);
    const showPopout = !this.implicit && !this.static && (this._keyboardOpen || this._mouseHover);
    const getAccessibleTableDescription = () => {
      return [this._isNonZeroTotal ? `${language.translations.arrowAt}: ${Math.round(100 * this.value)}%.` : `${language.translations.noArrow}.`, this._isNonZeroTotal && highlightedSegments.length ? `${language.translations.highlightedSegments}: ${highlightedSegments.map(x => `"${x.name}"`).join(', ')}.` : `${language.translations.noHighlightedSegments}.`].join(' ');
    };
    const renderInnerContent = async () => {
      await new Promise(resolve => requestAnimationFrame(resolve));
      const renderShape = () => {
        switch (this.shape) {
          case 'arc':
            return ShapeArc.call(this, {
              indication,
              metadata,
              distribution
            });
          case 'bar':
            return ShapeBar.call(this, {
              indication,
              metadata,
              distribution
            });
          default:
            return null;
        }
      };
      return html`
        ${renderShape()}
        ${showPopout ? DistributionPopout({
        name: this.label,
        unit: this.unit,
        metadata: metadata,
        values: distribution,
        referenceElement: this.shadowRoot!.querySelector('svg')!
      }) : nothing}
        ${this.static ? DistributionStaticLegend({
        unit: this.unit,
        metadata: metadata,
        values: distribution
      }) : null}
        ${DistributionAccessibleTable({
        metadata: metadata,
        data: {
          name: this.label,
          values: distribution
        },
        unit: this.unit,
        locale: language.locale,
        description: getAccessibleTableDescription()
      })}
      `;
    };
    return html`<div
      class="one-ux-element--root"
      role="group"
      aria-roledescription=${this.#getRoleDescription(language)}
      aria-label="${this.label}"
      tabindex=${ifDefined(this.implicit ? undefined : 0)}
    >
      ${until(renderInnerContent())}
    </div>`;
  }
  #extractMetadataAndDistribution = (indication: indicationData[]) => {
    const metadata = indication.map((x, i) => ({
      key: String(i),
      name: x.name,
      color: x.color,
      icon: x.icon,
      exclude: x.exclude
    }));
    const distribution = indication.reduce((result: any, item, i) => {
      result[String(i)] = item.value;
      return result;
    }, {});
    return {
      metadata,
      distribution
    };
  };
  #handleKeydown = (event: KeyboardEvent) => {
    const handled = () => {
      event.stopPropagation();
      event.preventDefault();
    };
    switch (event.code) {
      case keyCodes.RETURN:
      case keyCodes.SPACE:
        this._keyboardOpen = !this._keyboardOpen;
        handled();
        break;
      case keyCodes.ESCAPE:
        this._keyboardOpen = false;
        handled();
        break;
    }
  };
  #getRoleDescription = (language: language) => {
    switch (this.shape) {
      case 'arc':
        return language.translations.indicationArc;
      case 'bar':
        return language.translations.indicationBar;
    }
    throw new Error('Missing role description');
  };
}